import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ValidationErrors } from '@angular/forms';
import {
    ClientManagerInstanceToken,
    ClientManagerInterface,
    ConfigService,
    ConfirmationTimeRecordingModel,
    EventService,
    TIMERECORDING_TYPE_OF_WORK_CODES,
    TimeRecordingGeneralEntryModel,
    TimeRecordingValidationResultModel,
    TimeRecordingValidationService,
    UiResponsiveService,
} from '@evorait/pace-core';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'pace-add-time-recording-entry-form',
    templateUrl: './add-time-recording-entry-form.component.html',
    styleUrls: ['./add-time-recording-entry-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddTimeRecordingEntryFormComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input()
    public timeRecordingModel: ConfirmationTimeRecordingModel;

    @Input()
    public parentForm: FormArray;

    @Output()
    public timeRecordingSaved = new EventEmitter<TimeRecordingGeneralEntryModel>();

    @Output()
    public changesDiscarded = new EventEmitter<void>();

    public timeRecordingAddNewEntryForm: FormGroup;
    public timeRecordingAddNewEntryFormChildren: FormArray;

    // class members
    public model: TimeRecordingGeneralEntryModel;
    public displayActivityDropdown = false;
    public displayBillableDropdown: boolean;
    public displayActivities: boolean;
    public displayAdditionalTravelInfo: boolean;

    public falsyValidationResults: TimeRecordingValidationResultModel[] = [];
    private destroy$ = new Subject<void>();
    public isMobile$: Observable<boolean>;
    public constructor(
        private readonly formBuilder: FormBuilder,
        private readonly translate: TranslateService,
        private readonly timeRecordingValidationService: TimeRecordingValidationService,
        private readonly eventService: EventService,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly configService: ConfigService,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        private readonly uiResponsiveService: UiResponsiveService,
    ) {
        this.displayBillableDropdown = this.clientManager.isNgmP58();
        this.displayActivities = this.clientManager.isNgmP58();
        this.eventService.timeRecordingValidationChanged$.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.timeRecordingAddNewEntryForm.updateValueAndValidity({ onlySelf: true });
            this.changeDetectorRef.detectChanges();
        });
        this.eventService.timeRecordingBillableOptionChanged$.pipe(takeUntil(this.destroy$)).subscribe(() => {
            const presetValue = this.timeRecordingModel.billablePresetModel.selectedEntry;
            if (presetValue) {
                this.model.billableModel.selectByEntry(presetValue.model);
            }
        });
    }

    public ngOnInit(): void {
        this.displayAdditionalTravelInfo = this.configService.getDisplayAdditionalTravelInfo();
        const now = moment().startOf('minute');
        this.model = this.timeRecordingModel.createNewGeneralTimeRecordingModel(
            now.clone(),
            now.clone(),
            undefined,
            false,
            undefined,
        );
        // to check if there is preset value available
        const presetValue = this.timeRecordingModel.billablePresetModel.selectedEntry;
        if (presetValue) {
            this.model.billableModel.selectByEntry(presetValue.model);
        }
        if (this.clientManager.isNgmAA()) {
            this.model.typeOfWorkModel.value = this.model.typeOfWorkModel.value.filter(
                (typeOfWork) => typeOfWork.value !== TIMERECORDING_TYPE_OF_WORK_CODES.PHONE_FIX,
            );
        }

        const timeRecordingAddNewEntryFormValidator = (): ValidationErrors | null => this.checkValidity();
        this.timeRecordingAddNewEntryFormChildren = this.formBuilder.array([]);
        this.timeRecordingAddNewEntryForm = this.formBuilder.group(
            {
                children: this.timeRecordingAddNewEntryFormChildren,
            },
            {
                validators: timeRecordingAddNewEntryFormValidator,
            },
        );

        this.parentForm.push(this.timeRecordingAddNewEntryForm);
        this.isMobile$ = this.uiResponsiveService.isMobile$;
    }

    public ngAfterViewInit(): void {
        this.timeRecordingAddNewEntryForm.updateValueAndValidity();
    }

    public ngOnDestroy(): void {
        this.destroy$.next();

        // remove form controls from parent
        const index = this.parentForm.controls.findIndex((control) => control === this.timeRecordingAddNewEntryForm);
        this.parentForm.removeAt(index);
    }

    public onStartDateTimeChange(): void {
        this.model.updateEndDateTime();
        this.timeRecordingAddNewEntryForm.updateValueAndValidity();
    }

    public onTypeOfWorkChange(): void {
        // make the preset dropdown be changeable again as might have been disabled by the "phone fix process"
        this.model.enableBillableDropdown();
        this.displayActivityDropdown = this.model.isActivityRequired();

        if (this.model.isPhoneFix()) {
            // It is not allowed to change the billing code in case of "phone fix"
            this.model.disableBillableDropdown();
        }
        this.timeRecordingAddNewEntryForm.updateValueAndValidity();
    }

    public onEndDateTimeChange(): void {
        if (this.model.endDateTime.isBefore(this.model.startDateTime)) {
            this.model.startDateTime = this.model.endDateTime.clone().startOf('minute');
        }
        this.timeRecordingAddNewEntryForm.updateValueAndValidity();
    }

    private checkValidity(): ValidationErrors | null {
        const errors: ValidationErrors = {};

        const validationResult = this.timeRecordingValidationService.performNewEntryValidation(
            this.timeRecordingModel.timeRecordingEntries.value,
            this.model,
            this.timeRecordingModel.isNotificationRemoteWork,
        );
        this.falsyValidationResults = validationResult.filter((validationEntry) => !validationEntry.isValid);

        if (this.falsyValidationResults.length > 0) {
            errors[this.falsyValidationResults[0].validationErrorKey] = this.translate.instant(
                this.falsyValidationResults[0].validationErrorMessage,
            );
        }

        return Object.keys(errors).length > 0 ? errors : null;
    }

    public save(): void {
        this.timeRecordingSaved.emit(this.model);
        this.discard();
    }

    public discard(): void {
        this.changesDiscarded.emit();
    }
}
